/**
 * Sessions List Page — "My Sessions"
 *
 * Redesign (2026-06): restyled to align with the dashboards (warm .otter-bg, Fredoka/Nunito,
 * chip header, warm Start hero, rounded cards). The start/resume FLOW is unchanged
 * (useStartSession → /session/:id, seamless conflict-redirect, demo cap, allowedMode gate).
 *
 * Product change (per request): no "ask a grown-up" / selling. Instead the page shows a
 * neutral "X sessions remaining" count for limited (free/demo) students using existing data,
 * and a plain "0 sessions remaining" when out (free limit reached OR a paid family's 402).
 * No coins, prices, or top-up language anywhere (CCM-03 preserved — sessions-framed only).
 *
 * Preserved contracts: header role=banner + AppLogo + Sign out; "Start a new session" button
 * (aria-label "Start a new tutoring session") + "Starting session..." loading; ResumeSessionHero
 * ("You have an open session" / "Finish this session before you can start a new one") when an
 * active session exists; start error + "Try again"; SessionsList (active + completed).
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { useSessions } from '@/hooks/useSessions';
import { useAuth } from '@/hooks/useAuth';
import { getPostLoginRoute } from '@/constants/routes';
import { usePebbles } from '@/hooks/usePebbles';
import { useStudentConfig } from '@/hooks/useStudentConfig';
import { useStartSession } from '@/hooks/useStartSession';
import { useDemoLimits } from '@/hooks/useDemoLimits';
import { AppLogo } from '@/components/AppLogo';
import { StudentProfileHeader } from '@/components/StudentProfileHeader';
import { PebblesDisplay } from '@/components/PebblesDisplay';
import { ProfileIcon } from '@/components/ProfileIcon';
import { LogoutButton } from '@/components/LogoutButton';
import { SessionsList } from '@/components/SessionsList';
import { ResumeSessionHero } from '@/components/ResumeSessionHero';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import { Button } from '@/components/ui/button';

export function SessionsListPage() {
  const { sessions, activeSessions, completedSessions, isLoading, error, refetch } = useSessions();
  // Back link target follows the viewer's role rather than the deprecated static
  // /dashboard: parents -> /parent, students -> /dashboard. Delegates the fork to
  // getPostLoginRoute (the same helper used by AppLogo / RootRedirect / LoginPage)
  // so the precedence rule stays in one place (TO-LL-013: no hardcoded paths).
  const { user } = useAuth();
  const backTarget = getPostLoginRoute(user?.groups);
  const { totalPebbles, profile, isLoading: pebblesLoading } = usePebbles();
  const queryClient = useQueryClient();
  const { allowedMode, isLoading: configLoading } = useStudentConfig();
  const {
    startSession,
    isStarting,
    error: startError,
    isSuccess,
    reset: resetStartError,
    isOutOfCoins,
  } = useStartSession();

  // Sticky no-sessions flag — set by useStartSession after a 402. Combined with the
  // mutation-state flag so the count does not flash back after navigation.
  const { data: stickyOutOfCoins } = useQuery<boolean>({
    queryKey: ['family', 'outOfCoins'],
    queryFn: () => false,
    staleTime: Infinity,
  });
  const outOfCoins = isOutOfCoins || stickyOutOfCoins === true;

  // Lifetime session cap for limited (free/demo) users — existing data, sessions-framed.
  const { isFreeUser, maxSessions, isLoading: limitsLoading } = useDemoLimits();
  const totalSessions = (sessions ?? []).length;
  const hasReachedLimit = isFreeUser && maxSessions !== null && totalSessions >= maxSessions;
  const remaining = maxSessions !== null ? Math.max(0, maxSessions - totalSessions) : 0;

  const canStartSession =
    (allowedMode === 'serverless' || allowedMode === 'both') && !hasReachedLimit;
  const hasActiveSession = activeSessions.length > 0;

  // Out of sessions: a limited student has hit their cap, OR a paid family hit the 402.
  const noSessionsLeft = (outOfCoins || hasReachedLimit) && !hasActiveSession;

  // Show the "X sessions remaining" badge for limited users where the count exists (free/demo).
  // Hidden once out (the out-state card carries the "0 sessions remaining" message instead).
  const showRemainingBadge =
    !isLoading && !error && isFreeUser && !limitsLoading && maxSessions !== null && !noSessionsLeft;

  // For premium students: sessions available is backend-computed (parent coins ÷ session cost).
  // Null when unlimited plan, no parent link, or not applicable. Hidden when outOfCoins (the
  // "0 sessions remaining" card handles that state) and when sessionsAvailable is 0.
  const premiumSessionsAvailable = profile?.sessionsAvailable ?? null;
  const showPremiumSessionsBadge =
    !isLoading && !error && !isFreeUser && !pebblesLoading &&
    premiumSessionsAvailable !== null && premiumSessionsAvailable > 0 && !outOfCoins;

  useEffect(() => {
    if (isSuccess) {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      queryClient.invalidateQueries({ queryKey: ['student', 'config'] });
    }
  }, [isSuccess, queryClient]);

  return (
    <div className="otter-bg font-body min-h-screen text-otter-earth-800">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-otter-sand bg-otter-paper/80 px-3 py-2 backdrop-blur-md md:px-6 md:py-3">
        <div className="flex items-center gap-2">
          <Link
            to={backTarget}
            className="inline-flex items-center gap-0.5 rounded-lg px-2 py-1.5 text-sm font-bold text-pebbles-500 transition-colors hover:bg-otter-sand/40 hover:text-otter-earth-800"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back</span>
          </Link>
          <AppLogo />
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 shadow-sm">
            {pebblesLoading ? (
              <span className="text-base text-gray-400">-</span>
            ) : (
              <PebblesDisplay totalPebbles={totalPebbles} size="sm" />
            )}
          </span>
          <span className="inline-flex items-center rounded-full border border-otter-sand bg-white px-2 py-1 shadow-sm">
            <StudentProfileHeader
              displayName={profile?.displayName || ''}
              profileImageUrl={profile?.profileImageUrl}
              onUploadSuccess={() => {
                queryClient.invalidateQueries({ queryKey: ['user', 'profile'] });
              }}
            />
          </span>
          <LogoutButton />
        </div>
      </header>

      {/* ── Main ────────────────────────────────────────────────────────────── */}
      <main className="mx-auto max-w-4xl">
        <div className="px-4 pb-8 pt-4 md:px-6 md:pt-7">
          <h1 className="font-display text-2xl font-semibold leading-tight text-otter-earth-800 md:text-[32px]">
            My Sessions
          </h1>
          <p className="mt-1 text-sm font-medium text-gray-500 md:text-[15px]">
            Start a new session, or revisit a past one.
          </p>

          {/* "X sessions remaining" badge — limited (free/demo) users only (sessions-framed) */}
          {showRemainingBadge && (
            <div
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-bold text-otter-earth-700 shadow-sm"
              role="status"
              aria-label={`${remaining} sessions remaining`}
            >
              <span aria-hidden="true">🎟️</span>
              <span className="font-display text-lg leading-none text-otter-orange-600">
                {remaining}
              </span>
              sessions remaining
            </div>
          )}

          {/* "X sessions remaining" badge — premium students, coin-based (sessions-framed, no coin language per CCM-03) */}
          {showPremiumSessionsBadge && (
            <div
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-bold text-otter-earth-700 shadow-sm"
              role="status"
              aria-label={`${premiumSessionsAvailable} sessions remaining`}
            >
              <span aria-hidden="true">🎟️</span>
              <span className="font-display text-lg leading-none text-otter-orange-600">
                {premiumSessionsAvailable}
              </span>
              sessions remaining
            </div>
          )}

          {/* Loading */}
          {isLoading && <LoadingState message="Loading your sessions..." />}

          {/* Error */}
          {error && !isLoading && (
            <ErrorState
              message="Failed to load sessions. Please check your connection and try again."
              onRetry={refetch}
            />
          )}

          {/* Content */}
          {!isLoading && !error && (
            <div className="mt-5">
              <SessionsList
                completedSessions={completedSessions}
                startSessionButton={
                  // Active session leads — the resume hero is the SINGLE active-session
                  // affordance (shown even at the cap so the student can always continue).
                  hasActiveSession ? (
                    <ResumeSessionHero activeSession={activeSessions[0]} />
                  ) : noSessionsLeft ? (
                    // Child-friendly "0 sessions remaining" that nudges telling a parent
                    // (no in-app selling). Supersedes the earlier neutral CCM-03 wording.
                    <div
                      className="mb-2 flex flex-col items-center rounded-3xl border border-otter-sand bg-white/70 py-8 text-center shadow-sm"
                      role="status"
                    >
                      <span aria-hidden="true" className="mb-2 text-3xl">
                        🎟️
                      </span>
                      <p className="font-display text-lg font-semibold text-otter-earth-800">
                        0 sessions remaining
                      </p>
                      <p className="mt-1 text-sm text-gray-500">You&apos;ve finished all your sessions for now. Ask a parent to unlock more.</p>
                    </div>
                  ) : !configLoading && canStartSession ? (
                    // Warm "Start a new session" hero (the same useStartSession button, restyled).
                    <div className="relative mb-2 flex flex-col overflow-hidden rounded-3xl border border-orange-200 bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 p-6 shadow-lg">
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-otter-orange-400/20 blur-2xl"
                      />
                      <p className="relative z-[1] mb-4 text-[11px] font-extrabold uppercase tracking-wider text-otter-orange-600">
                        <span aria-hidden="true">🦦</span> New session
                      </p>
                      <div className="relative z-[1] flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <ProfileIcon
                            imageUrl={profile?.profileImageUrl}
                            size="lg"
                            className="border-4 border-white shadow-md"
                          />
                          <div className="min-w-0">
                            <h2 className="font-display text-xl font-semibold leading-tight text-otter-earth-800">
                              Ready for a new reading session?
                            </h2>
                            <p className="mt-1 text-[13px] font-semibold text-orange-700/80">
                              Your tutor has a fresh passage waiting for you.
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="warm"
                          size="lg"
                          onClick={() => startSession()}
                          disabled={isStarting}
                          className="h-12 px-6"
                          aria-busy={isStarting}
                          aria-label="Start a new tutoring session"
                        >
                          {isStarting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Starting session...
                            </>
                          ) : (
                            'Start a new session'
                          )}
                        </Button>
                      </div>
                      {startError && (
                        <div className="relative z-[1] mt-4" role="alert" aria-live="polite">
                          <p className="text-sm text-red-600">{startError}</p>
                          <button className="mt-1 text-sm text-tutor-600 underline" onClick={resetStartError}>
                            Try again
                          </button>
                        </div>
                      )}
                    </div>
                  ) : null
                }
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
